import { IScope, IQService, IPromise, IAngularEvent, gridster, ITimeoutService } from "angular";
import { StateService, TransitionService, Transition } from "@uirouter/angularjs";
import { IModalService } from "angular-ui-bootstrap";
import YAML from "yaml";
import { HotkeysProvider } from "angular-hotkeys";
import dayjs from "@gtmhub/dayjs";
import {
  IInsightboard,
  IInsightsIdMap,
  IInsightboardItem,
  IInsightboardFiltersUsage,
  IInsightboardWidgets,
  IReportFilter,
  IInsightboardTransactions,
  IInsightboardItemSetting,
  IInsightDetails,
  IInsightboardItemSettingName,
  IRemovedInsightboardParametersMap,
  IInsightboardSettingsChange,
  IInsightTemplateSuggestionSetting,
  IInsightboardItemView,
  IInsightBoardItemResponse,
} from "../models/models";
import { IInsightFilters, IInsight, IInsightMetricUsageMap } from "../../insights/models/models";
import { IAssigneeIdMap } from "../../assignees/models";
import { ICachedDynamicEntitiesMap, IParameter } from "../../parameters/models";
import { InsightboardService } from "../services/dashboardService";
import { InsightService, isInsightGoogleChartDep, getGoogleCharts } from "@gtmhub/insights";
import { CommentService, IComment, ICommentStatsMap } from "@gtmhub/comments";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { localize } from "@gtmhub/localization";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { HubService } from "@gtmhub/hub/services/hub";
import { AnalyticsService, Flavor, IRecommendationArgs, recommendation } from "@gtmhub/core/analytics";
import { DocumentExporterService, IInsightBoardPdfQuery } from "@gtmhub/document-exporter/document-exporter-service";
import { ICollection, IGtmhubRootScopeService } from "@gtmhub/models";
import { INgRedux } from "@gtmhub/state-management";
import { IStateInit } from "@gtmhub/core/routing";
import { NameMap, toIdMap, toKeyMap } from "@gtmhub/util";
import { IExtendedRootScopeService } from "../../shared/ng-extensions/models";
import { AssigneeActions } from "../../assignees/redux/assignee-actions";
import { toTeamAssigneeIdMap } from "../../assignees/redux/assignee-selectors";
import { ITeamsIdMap } from "../../teams/models/models";
import { IAllowedActionsMap, IPlanningSession, ISessionsIdMap, ISessionsStoreState, IUserItemAllowedActions, PlanningSessionsActions } from "@gtmhub/sessions";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { IGridFilter, GridFilterDefinitions, createFilter, IGridFiltersChange, IGridFilterDefinition } from "@gtmhub/shared/components/grid-filtering-bar";
import { filterFactory, toGridFilterEntityDataItems, createInsightboardSettingFromGridFilter } from "../filter-utils";
import { DataSourceService } from "@gtmhub/datasources/services/dataSourceService";
import { IDataSourceBlueprint, IDataflowDataSource, IGetDataFlowParams } from "@gtmhub/datasources/models/models";
import { triggerFileDownload } from "@gtmhub/shared/dom";
import { InsightDependencies } from "@gtmhub/insights/services/insightDependencies";
import { IInsightCalculationData, MessagingService } from "@gtmhub/sockets";
import { IHubFilter } from "@gtmhub/hub/models/hub";
import { InsightTemplateSuggestionsService } from "../services/insight-template-suggestions.service";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { IDropdownButtonMenuItem } from "@gtmhub/shared/components";
import { SectionHeaderService } from "@gtmhub/insightboards/services/sectionHeaderService";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { sortInsightBoardItems } from "../util";
import { ViewHistoryService } from "@gtmhub/view-history";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { CurrentUserService, IDashboardSetting } from "@gtmhub/users";

export interface IInsightboardRootScopeService extends IGtmhubRootScopeService {
  filters: { [name: string]: string }; // used in the insight-filters directive
  cast: boolean;
  $root: IInsightboardRootScopeService;
}

interface IUpdatedFilter {
  filterKey: string;
  value: string;
  cachedDynamicEntitiesMap: ICachedDynamicEntitiesMap;
  updatedValueDisplayName?: string;
}

export interface IInsightboardParams {
  dashboardId: string;
  sessionId: string;
  widgets?: IInsight[];
}

interface IInsightResponse {
  transactionID: string;
  data: IInsightCalculationData;
}

interface IEnrichedWidget {
  widget: HTMLElement;
  top: number;
  left: number;
}

interface IGridStates {
  insightIndex: number;
  columnsOccupied: number;
  maxRowHeight: number;
  rowsCount: number;
  columns: number;
  rowHeight: number;
}

interface IGridEvent extends IAngularEvent {
  pageX: number;
  pageY: number;
  pointerId: number;
  target: HTMLElement;
  which: number;
}

interface ITransactionsMap {
  [transactionId: string]: string;
}

export interface IInsightboardScope extends IScope, IExtendedRootScopeService {
  insightsLoaded: boolean;
  loadMoreInsights(): void;
  limit: number;

  indicators: {
    insightboard: boolean;
    savingFilters: boolean;
    exporting: boolean;
  };

  insightboard: IInsightboard;
  gridsterOptions: gridster.GridsterConfig;

  selectedParameters: IInsightFilters;
  mode;
  focusedInsight;
  filterChanged: boolean;
  allowedActionsMap: IAllowedActionsMap;
  insightsIdMap: IInsightsIdMap;
  assigneesIdMap: IAssigneeIdMap;
  teamsIdMap: ITeamsIdMap;
  translatedReportName: string;
  sessionIdMap: ISessionsIdMap;
  heatmapStart: Date; // exact name match is required by report html coming from API to build heatmap date start

  allSocketsReceived: boolean;
  foundSession: IPlanningSession;
  areReduxItemsFetched: boolean;
  dataFlowBlueprints: IDataSourceBlueprint[];
  dataFlowDataSources: {
    [id: string]: IDataflowDataSource;
  };
  insightboardActionItems: IDropdownButtonMenuItem[];
  hasExistingSessionInStateParams: boolean;
  saveFilters();
  enterEditMode();
  createInsight(): void;
  focus(insightTag);
  saveInsightboardItemsAndEditWidget(widget: IInsightboardItem): void;
  deleteWidget(index: number, widget: IInsightboardItem): void;
  cloneInsight(widget: IInsightboardItem);
  processChanges(exitEditMode: boolean, widgetToBeEdited: IInsightboardItem);
  exitEditMode(revertToOriginal: boolean);
  redirectTo(widget: IInsightboardItem): void;
  exportToPdf(): void;
  toggleCastMode(): void;
  directSubmitOfInsightboardToMarketplace(): void;
  removeBorderOnFocus(widgetSelector: string): void;
  revertBorderOnBlur($event: JQueryEventObject, widgetSelector: string): void;
  onKeyDownSectionHeader($event: JQueryEventObject): void;
  cancelEdit(): void;
  setInputWidthForSectionHeaderTitle(text: string): string;
  setInputWidthForSectionHeaderSubTitle(text: string): string;
  filters: IGridFilter[];
  filterDefinitions: GridFilterDefinitions;
  filtersChange(change: IGridFiltersChange): void;
  showAddInsightsTooltip: boolean;
  shouldShowInsightSuggestions: boolean;
  showChangesSavedMessage: boolean;
  insightSuggestionSettings: IInsightTemplateSuggestionSetting;
  dismissTooltip(): void;
  isImprovePdfExportsEnabled: boolean;
  addToFavorites(): void;
  removeFromFavorites(): void;
  isFavorite(): boolean;
}

const LOAD_MORE_INSIGHTS_BY = 3;

export class InsightboardCtrl implements IStateInit {
  public static $inject = [
    "hotkeys",
    "$rootScope",
    "$transitions",
    "$scope",
    "$state",
    "$stateParams",
    "$uibModal",
    "$q",
    "InsightboardService",
    "InsightService",
    "CommentService",
    "PermissionsService",
    "UIErrorHandlingService",
    "HubService",
    "AnalyticsService",
    "DocumentExporterService",
    "$ngRedux",
    "PlanningSessionsActions",
    "AssigneeActions",
    "DataSourceService",
    "InsightDependencies",
    "MessagingService",
    "InsightTemplateSuggestionsService",
    "SectionHeaderService",
    "FeatureToggleV2Service",
    "$timeout",
    "ViewHistoryService",
    "FavoritesManagerFactory",
    "CurrentUserService",
  ];

  gridStates: IGridStates;
  previousFiltersMap: Record<string, IInsightboardItemSetting>;
  private favoritesManager: FavoritesManager;

  constructor(
    private hotkeys: HotkeysProvider,
    private $rootScope: IInsightboardRootScopeService,
    private $transitions: TransitionService,
    private $scope: IInsightboardScope,
    private $state: StateService,
    private $stateParams: IInsightboardParams,
    private $uibModal: IModalService,
    private $q: IQService,
    private insightboardService: InsightboardService,
    private insightService: InsightService,
    private commentService: CommentService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private hubService: HubService,
    private analyticsService: AnalyticsService,
    private documentExporterService: DocumentExporterService,
    private $ngRedux: INgRedux,
    planningSessionsActions: PlanningSessionsActions,
    assigneeActions: AssigneeActions,
    private dataSourceService: DataSourceService,
    private insightDependencies: InsightDependencies,
    private messagingService: MessagingService,
    private insightTemplateSuggestionsService: InsightTemplateSuggestionsService,
    private sectionHeaderService: SectionHeaderService,
    private featureToggleV2Service: FeatureToggleV2Service,
    private $timeout: ITimeoutService,
    private viewHistoryService: ViewHistoryService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private currentUserService: CurrentUserService
  ) {
    if ($rootScope.newExperienceEnabledForUser) {
      this.favoritesManager = this.favoritesManagerFactory(["dashboard"]);
      this.favoritesManager.initFavorites();
    }

    $ngRedux.dispatch(planningSessionsActions.getSessionsIfMissing());
    $ngRedux.dispatch(assigneeActions.getAssignees());

    this.$scope.$on("commentPosted", this.onCommentPosted);
    this.$scope.$on("commentDeleted", this.onCommentDeleted);
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  public getTransactionsMap(): ITransactionsMap {
    return this.transactionsMap;
  }

  private initialBoardFields =
    "id,insights{col,filters,id,insightId,insightName,insightTitle,row,sizeX,sizeY,insightState,insightSource,insightRequiredEntities,formatting,type,sectionHeaderId},name," +
    "sectionHeaders{col,id,row,sizeX,sizeY,sectionHeader,type,sectionHeaderId}," +
    "parameters{defaultValue,entityName,entityTitleField,entityValueField,id,key,predefinedValues,selectorType,title}," +
    "readOnly,settings{name,value,valueDisplayName,dynamicValues},tags{name,title},isReport";

  private paramsAndSettingsBoardFields =
    "parameters{defaultValue,entityName,entityTitleField,entityValueField,id,key,predefinedValues,selectorType,title}," +
    "settings{name,value,valueDisplayName,dynamicValues}";

  private insightsFields = "createdBy{name,picture,email,id},description,id,name,tags{name,title},title,usedFilters,view,formatting,state,source";

  private dataFlowFields =
    "blueprint{id,name,title,logoUrl},connections{dataSources{" +
    "dataSource{key,id,name,lastSyncTime,lastSyncResult,syncFailureReason,uiStatus}," +
    "entities{id,name,title,status,failureReason,lastStatusChange,lastStatusChange}}}";

  /*
  Map with key insightboard parameter (also called filter)
  and value that is array of the insight ids in the insightboard that have the parameter included
  */
  private insightsParametersUsageMap: IInsightboardFiltersUsage = {};

  private transactionsMap: ITransactionsMap = {};
  private unhandledResponses: Array<IInsightResponse> = [];

  private initialWidgets: IInsightboardWidgets = {
    array: null,
    map: null,
  };

  private isInsightRenderingOptimisationsEnabled = false;
  private persistInsightboardFilters = false;

  private strings = {
    read: "read",
    update: "update",
    modifyPermissions: "modifyPermissions",
    report: "report",
    session: "session",
    team: "team",
    defaultValueDisplayName: "None selected",
    insightState: "gtmhub.insight",
    insightCodelessEditorState: "gtmhub.insightCodelessEditor",
  };

  private reportFilter: IReportFilter = {
    name: null,
    filterType: this.strings.report,
    dashboardItemsSettings: null,
  };

  // filter settings' 'name' should match insightboard.parameters' 'key' if they are to match
  // default to this report filter if insightboard is of type 'report' and user has no filter for this report
  // name will be matched with the name of the insightboard once insightboard is returned from backend
  private defaultReportFilter: IReportFilter = {
    name: "",
    filterType: this.strings.report,
    dashboardItemsSettings: [
      {
        name: this.strings.session,
        value: "",
        valueDisplayName: this.strings.defaultValueDisplayName,
      },
      {
        name: this.strings.team,
        value: "",
        valueDisplayName: this.strings.defaultValueDisplayName,
      },
    ],
  };

  private insightBoardInsightConnections: number = null;

  private updateInsightResultBuffer: IInsightResponse[] = [];

  private bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState) => {
    const sessions = state.sessions.items;

    return {
      sessionIdMap: toIdMap(sessions),
      foundSession: sessions.filter((s) => s.status !== "closed").find((s) => s.id === this.$stateParams.sessionId),
      assigneesIdMap: state.assignees.map,
      teamsIdMap: toTeamAssigneeIdMap(state),
      areReduxItemsFetched: state.sessions.isFetched && state.assignees.isFetched,
    };
  };

  private init = (): IPromise<unknown> => {
    const unsubscribe = this.$ngRedux.connect(this.bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$scope.insightsIdMap = {};

    this.$scope.dataFlowBlueprints = [];

    this.$scope.dataFlowDataSources = {};

    this.$scope.indicators = {
      insightboard: false,
      savingFilters: false,
      exporting: false,
    };

    this.$scope.insightboardActionItems = [
      {
        e2eData: "create-insight",
        textKey: "insight",
        descriptionKey: "create_insight_dropdown_subtitle",
        action: {
          handler: () => {
            this.$state.go(".widgets.browser", { widgetsDashboardId: this.$scope.insightboard.id });
            // Recommendation is not helpful if user choses to manually add an insight if there are no insights on a board
            if (this.$scope.insightboard.insightBoardItems.length === 0) {
              this.trackRecommendationHelpfulness({ isUseful: false });
            }
          },
        },
        imageSrc: require("wwwroot/img/icons/insight3.svg"),
      },
      {
        e2eData: "create-section-header",
        textKey: "section_header",
        descriptionKey: "section_header_subtitle",
        action: {
          handler: () => {
            this.addSectionHeader();
          },
        },
        imageSrc: require("wwwroot/img/icons/section-header.svg"),
      },
    ];

    this.$rootScope.noNavigation = false;

    this.$scope.filtersChange = this.filtersChange;
    this.$scope.saveInsightboardItemsAndEditWidget = this.saveInsightboardItemsAndEditWidget;
    this.$scope.cloneInsight = this.cloneInsight;
    this.$scope.exitEditMode = this.exitEditMode;
    this.$scope.enterEditMode = this.enterEditMode;
    this.$scope.createInsight = this.createInsight;
    this.$scope.deleteWidget = this.deleteWidget;
    this.$scope.saveFilters = this.saveFilters;
    this.$scope.processChanges = this.processChanges;
    this.$scope.focus = this.focus;
    this.$scope.redirectTo = this.redirectTo;
    this.$scope.toggleCastMode = this.toggleCastMode;
    this.$scope.loadMoreInsights = this.loadMoreInsights;
    this.$scope.directSubmitOfInsightboardToMarketplace = this.directSubmitOfInsightboardToMarketplace;
    this.$scope.removeBorderOnFocus = this.removeBorderOnFocus;
    this.$scope.revertBorderOnBlur = this.revertBorderOnBlur;
    this.$scope.onKeyDownSectionHeader = this.onKeyDownSectionHeader;
    this.$scope.cancelEdit = this.cancelEdit;
    this.$scope.setInputWidthForSectionHeaderTitle = this.setInputWidthForSectionHeaderTitle;
    this.$scope.setInputWidthForSectionHeaderSubTitle = this.setInputWidthForSectionHeaderSubTitle;
    this.$scope.addToFavorites = this.addToFavorites;
    this.$scope.removeFromFavorites = this.removeFromFavorites;

    this.$scope.allSocketsReceived = false;

    this.$scope.exportToPdf = this.exportToPdf;

    this.$scope.focusedInsight = "";

    this.$scope.insightsLoaded = false;

    this.$scope.showChangesSavedMessage = false;

    this.$scope.$on(
      "$destroy",
      this.$transitions.onSuccess(
        {
          from: "gtmhub.insightboard.widgets.browser",
          to: "gtmhub.insightboard",
        },
        (trans) => {
          this.onStateChanged(trans);
        }
      )
    );

    this.$scope.$on("insightboardTagsChanged", this.onInsightboardTagsChanged);
    this.$scope.$on("insightboardEdited", this.onInsightboardEdited);
    this.$scope.$on("insightRemoved", this.onInsightRemoved);

    this.$scope.$on("insightResponse", this.onInsightResponse);

    this.configureGrid();

    this.$scope.$on("insightTemplateSuggestionInstalled", this.onInsightSuggestionTemplateInstalled);
    this.$scope.$on("insightTemplateSuggestionDismissed", this.onInsightSuggestionTemplateDismissed);
    this.$scope.dismissTooltip = this.dismissTooltip;
    this.$scope.indicators.insightboard = true;

    this.getDataFlowItems();

    this.$scope.$watch("insightboard.insightBoardItems", (insightBoardItems: IInsightboardItem[]) => {
      this.$scope.gridsterOptions.maxRows = insightBoardItems ? insightBoardItems.length + 100 : this.$scope.gridsterOptions.maxRows;
    });

    return this.permissionsService
      .getCurrentUserAllowedActionsPerItem(this.$stateParams.dashboardId, "insightBoard")
      .then((actions: IUserItemAllowedActions) => {
        this.$scope.allowedActionsMap = this.constructAllowedActionsMap(actions);

        // if the current user is not allowed to view this board -> return and display no-permissions block
        if (!this.$scope.allowedActionsMap.read) {
          this.$scope.indicators.insightboard = false;
          return;
        }

        return this.$scope
          .watchUntil(() => this.$scope.areReduxItemsFetched)
          .then(() =>
            this.$q
              .all({
                insightboard: this.insightboardService.getInsightboard(this.$stateParams.dashboardId, this.initialBoardFields),
                filters: this.hubService.getFilters(this.strings.report),
                isModuleTasksFeatureEnable: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.ModuleTasks),
                isModuleTagsEnabled: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.ModuleTags),
                isInsightRenderingOptimisationsEnabled: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.EnableInsightRenderingOptimisations),
                isImprovePdfExportsEnabled: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.ImprovePdfExports),
                persistInsightboardFilters: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.PersistInsightboardFilters),
              })
              .then(
                ({
                  insightboard,
                  filters,
                  isModuleTasksFeatureEnable,
                  isModuleTagsEnabled,
                  isInsightRenderingOptimisationsEnabled,
                  isImprovePdfExportsEnabled,
                  persistInsightboardFilters,
                }) => {
                  this.setInsightboardPropertiesDependingOnFlag(insightboard, isModuleTasksFeatureEnable, "tasks", "people without krs", 4);
                  this.setInsightboardPropertiesDependingOnFlag(insightboard, isModuleTagsEnabled, "progress by tag", "tasks", 8);

                  this.$scope.isImprovePdfExportsEnabled = isImprovePdfExportsEnabled;
                  this.isInsightRenderingOptimisationsEnabled = isInsightRenderingOptimisationsEnabled;
                  this.persistInsightboardFilters = persistInsightboardFilters;
                  this.handleInsightboardAndFiltersResponses(insightboard, filters);
                  this.viewHistoryService.addItemToViewHistory(
                    this.viewHistoryService.payloadBuilder.withInsightboard({ id: insightboard.id }).forSource("navigation").generate(),
                    navItemsFactory.withRandomColor(navItemsFactory.buildInsightboardNavItem(insightboard))
                  );
                }
              )
          );
      })
      .catch((err) => {
        this.handleIndicatorError(err, "insightboard");
      });
  };

  private addToFavorites = () => {
    this.favoritesManager?.addToFavorites(this.$scope.insightboard as { id: string }, "dashboard", this.getNavItem());
  };

  private removeFromFavorites = () => {
    this.favoritesManager?.removeFromFavorites(this.$scope.insightboard as { id: string }, this.getNavItem());
  };

  private getNavItem = (): INavItem => {
    return this.favoritesManager?.getFavoriteItem({ id: this.$scope.insightboard.id }) || navItemsFactory.buildInsightboardNavItem(this.$scope.insightboard);
  };

  private trackRecommendationHelpfulness(args: IRecommendationArgs): void {
    //Tracking Insight creation recommendation helpfulness.
    this.analyticsService.track(recommendation, {
      is_useful: args.isUseful,
      flavor: Flavor.PASSIVE,
      action: "creates_insight",
    });
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private handleInsightboardAndFiltersResponses = (insightboard: IInsightboard, filters: IHubFilter[] | IReportFilter[]) => {
    this.gridStates = this.initGridStates();
    this.$scope.hasExistingSessionInStateParams = !!(this.$stateParams.sessionId && this.$scope.sessionIdMap[this.$stateParams.sessionId]);
    this.$scope.insightboard = insightboard;
    this.$scope.isFavorite = () => this.favoritesManager?.entityIsFavorite({ id: insightboard.id });

    insightboard.insightBoardItems = sortInsightBoardItems([...insightboard.sectionHeaders, ...insightboard.insights]);
    this.$scope.limit = this.setInitialRenderingLimits(insightboard.insightBoardItems);
    this.initialWidgets = this.deepCopyWidgets(this.$scope.insightboard.insightBoardItems);

    if (!this.$scope.insightboard.isReport) {
      this.configureHotkeys();

      const settingsChanges: IInsightboardSettingsChange = this.clearDeletedFilterValues(this.getFilterSettings());
      // remove deleted items from filter settings
      if (settingsChanges.settingsChanged) {
        this.insightboardService.editInsightboardSettings(this.$scope.insightboard.id, this.$scope.insightboard.name, settingsChanges.settings).then(() => {
          this.init();
        });
        return;
      }
    } else {
      this.reportFilter = this.getReportFilter(filters as IReportFilter[]);

      // report filters in contrast with insightboard filters are user-specific and are managed by the filters api (Shibuya)
      const settingsChanges: IInsightboardSettingsChange = this.clearDeletedFilterValues(this.getFilterSettings());
      // remove deleted items from filter settings
      if (settingsChanges.settingsChanged) {
        this.hubService.patchFilter(this.reportFilter.id, { dashboardItemsSettings: settingsChanges.settings }).then(() => {
          this.init();
        });
        return;
      }

      this.$scope.translatedReportName = this.translateReportName(this.$scope.insightboard.name);
    }

    // manually track page in the controller because of reports name
    this.trackPage(insightboard);

    // If a user has an update permission, we show insight template suggestions
    // Insight template suggestions settings are initialized if the board is empty
    if (this.$scope.allowedActionsMap.update && !this.$scope.insightboard.isReport) {
      const shouldInitializeEmptySettings = this.$scope.insightboard.insights.length == 0;
      const suggestionSettings = this.getSuggestionsSettings(this.$scope.insightboard.id, shouldInitializeEmptySettings);

      if (suggestionSettings) {
        const areSuggestionsDismissed = suggestionSettings.linkToMarketplaceDismissed && suggestionSettings.numberOfSuggestionsToShow == 0;
        if (!areSuggestionsDismissed) {
          this.$scope.insightSuggestionSettings = suggestionSettings;
          this.$scope.shouldShowInsightSuggestions = true;
        }
      }
    }
    // Insights which are added via stateParams, when we add insights via
    // insightboards -> widget-browser
    const addedInsights = this.$stateParams.widgets;

    // if there are no widgets -> do not get insights and calculate them
    if (this.$scope.insightboard.insights.length === 0) {
      this.addWidgetsFromStateParams(addedInsights);
      this.$scope.indicators.insightboard = false;
      return;
    }

    return this.getInsightsAndProcessCalculation().then(() => {
      // Make sure insights are loaded before processing parameters
      this.processParameters();
      this.$scope.heatmapStart = this.getHeatmapStart(this.$scope.sessionIdMap, this.$scope.selectedParameters);
      this.addWidgetsFromStateParams(addedInsights);
    });
  };

  private getInsightsAndProcessCalculation = (): IPromise<unknown> => {
    const insightIds: string = this.constructInsightIds();
    return this.insightService.getInsightsSafe(insightIds, this.insightsFields).then((insights) => {
      const hasGoogleChartDep = insights.items.some((insight) => isInsightGoogleChartDep(insight));
      if (hasGoogleChartDep) {
        return this.$q.when(getGoogleCharts()).then(() => this.processInsightsCalculation(insights, insightIds));
      }

      return this.processInsightsCalculation(insights, insightIds);
    });
  };

  private constructInsightIds = (): string => {
    return Object.keys(
      this.$scope.insightboard.insights.reduce<{ [insightId: string]: boolean }>(
        (
          idsSet,
          currentWidget
        ): {
          [insightId: string]: boolean;
        } => {
          idsSet[currentWidget.insightId] = true;

          if (currentWidget.filters && Array.isArray(currentWidget.filters)) {
            currentWidget.filters.forEach((filterKey: string): void => {
              if (!this.insightsParametersUsageMap[filterKey]) {
                this.insightsParametersUsageMap[filterKey] = [];
              }

              this.insightsParametersUsageMap[filterKey].push(currentWidget.insightId);
            });
          }

          return idsSet;
        },
        {}
      )
    ).join(",");
  };

  private constructAllowedActionsMap = (actions: IUserItemAllowedActions): IAllowedActionsMap => {
    return actions.currentUserActions.reduce((actionsMap: IAllowedActionsMap, currentAction: string): IAllowedActionsMap => {
      actionsMap[currentAction] = true;

      return actionsMap;
    }, {});
  };

  private initGridFilters = (insightboard: IInsightboard, reportFilter: IReportFilter) => {
    this.$scope.filters = [];
    this.$scope.filterDefinitions = {};

    let valueMap = this.$scope.insightboard.isReport ? toKeyMap("name", reportFilter.dashboardItemsSettings) : toKeyMap("name", insightboard.settings || []);

    if (this.persistInsightboardFilters) {
      const userDashboardSettings: IDashboardSetting[] | undefined = this.currentUserService.getUserSetting("dashboardsSettings");
      if (userDashboardSettings) {
        const userDashboardSetting: IDashboardSetting | undefined = userDashboardSettings.find((dashboardSetting) => dashboardSetting.dashboardId === insightboard.id);
        if (userDashboardSetting) {
          valueMap = toKeyMap("name", userDashboardSetting.settings || []);
          this.setFilterChanged(valueMap);
        }
      }
    }

    const parameters = insightboard.parameters || [];
    for (const parameter of parameters) {
      if (parameter.selectorType in filterFactory) {
        this.mapInsightboardParameterToGridFilter(valueMap, parameter);
      } else {
        console.warn(`Cannot map insightboard parameter with selector type '${parameter.selectorType}'`);
      }
    }
  };

  private mapInsightboardParameterToGridFilter = (valueMap: NameMap<IInsightboardItemSetting>, parameter: IParameter) => {
    const [definitionFactory, , toGridFilterValue] = filterFactory[parameter.selectorType];

    const definition = definitionFactory(parameter);
    definition.displayName = localize(definition.displayName);
    definition.defaultValue = toGridFilterValue(parameter, parameter.defaultValue);
    definition.readonly = (!this.$scope.allowedActionsMap[this.strings.update] || this.$scope.insightboard.readOnly) && !this.$scope.insightboard.isReport;

    if (definition.dataType === "date" && definition.predefinedDateRanges) {
      for (const dateRange of definition.predefinedDateRanges) {
        dateRange.displayName = localize(dateRange.displayName);
      }
    }

    this.$scope.filterDefinitions[definition.fieldName] = definition;

    const filter = this.createFilterFromDefinition(definition, valueMap, parameter);
    this.$scope.filters.push(filter);
  };

  private createFilterFromDefinition = (definition: IGridFilterDefinition, valueMap: NameMap<IInsightboardItemSetting>, parameter: IParameter): IGridFilter => {
    const [, , toGridFilterValue] = filterFactory[parameter.selectorType];

    const filter = createFilter(definition);
    if (parameter.key in valueMap) {
      this.$scope.selectedParameters[filter.fieldName] = valueMap[parameter.key].value;
      filter.value = toGridFilterValue(parameter, valueMap[parameter.key].value);
    }

    if (filter.dataType === "array" && definition.dataType === "array") {
      if (definition.arrayType === "entity") {
        filter.entityData = parameter.key in valueMap ? toGridFilterEntityDataItems(valueMap[parameter.key]) : [];
      }

      if (valueMap[parameter.key]) {
        filter.dynamicValues = valueMap[parameter.key].dynamicValues;
      }
    }

    return filter;
  };

  private filtersChange = (change: IGridFiltersChange) => {
    const valueMap: Record<string, IInsightboardItemSetting> = {};
    const parameterMap = toKeyMap("key", this.$scope.insightboard.parameters || []);

    for (const filter of this.$scope.filters) {
      const parameter = parameterMap[filter.fieldName];
      if (parameter) {
        const [, toInsightboardItemSetting] = filterFactory[parameter.selectorType];
        valueMap[filter.fieldName] = toInsightboardItemSetting(filter);
      } else {
        console.warn(`Cannot find insightboard parameter for grid filter: ${filter.fieldName}`, this.$scope.insightboard.parameters, filter);
      }
    }

    if (this.$scope.insightboard.isReport) {
      this.handleFiltersChangeForReport(valueMap);
    } else {
      this.$scope.selectedParameters = Object.entries(valueMap).reduce((map, [key, item]) => ({ ...map, [key]: item.value }), {});

      if (change.initial) {
        // initially we need to load all filters, not only the ones using a filter
        this.onFiltersChanged({});
      } else {
        this.setFilterChanged(valueMap);
        this.onFiltersChanged(valueMap);
        if (this.$scope.insightboard.id && this.persistInsightboardFilters) {
          const newDashboardSetting: IDashboardSetting = {
            dashboardId: this.$scope.insightboard.id,
            settings: Object.entries(valueMap).map((setting) => {
              return { name: setting[1].name, value: setting[1].value };
            }),
          };
          const dashboardSettingsUS: IDashboardSetting[] = this.currentUserService.getUserSetting("dashboardsSettings") ?? [];
          this.currentUserService.setUserSetting({
            dashboardsSettings: [...dashboardSettingsUS.filter((dashaboardSetting) => dashaboardSetting.dashboardId != this.$scope.insightboard.id), newDashboardSetting],
          });
        }
      }
    }

    this.previousFiltersMap = angular.copy(valueMap);
  };

  private getFilterChanges = (valueMap: Record<string, IInsightboardItemSetting>, previousMap: Record<string, IInsightboardItemSetting>): IInsightboardItemSetting[] => {
    if (!previousMap) {
      return Object.values(valueMap).map((v) => {
        return {
          name: v.name,
          value: v.value,
          valueDisplayName: v.valueDisplayName,
        };
      });
    }

    return Object.keys(valueMap).reduce((filtered, key) => {
      if (!previousMap[key] || valueMap[key].value !== previousMap[key].value) {
        filtered.push(valueMap[key]);
      }
      return filtered;
    }, []);
  };

  private setFilterChanged = (valueMap: Record<string, IInsightboardItemSetting>) => {
    const selectedFilterValues = Object.values(valueMap).map((v) => {
      return {
        name: v.name,
        value: v.value,
        dynamicValues: v.dynamicValues ? v.dynamicValues : [],
      };
    });

    const persistedInsightboardSettings = this.$scope.insightboard.settings.map((s) => {
      return {
        name: s.name,
        value: s.value,
        dynamicValues: s.dynamicValues ? s.dynamicValues : [],
      };
    });

    this.$scope.filterChanged = !angular.equals(selectedFilterValues, persistedInsightboardSettings);
  };

  private handleFiltersChangeForReport = (valueMap: Record<string, IInsightboardItemSetting>) => {
    if (this.reportFilter.dashboardItemsSettings) {
      for (const item of this.reportFilter.dashboardItemsSettings) {
        if (!(item.name in valueMap)) {
          item.value = "";
          item.valueDisplayName = "";
        }
      }
    }

    const updatedFilters = Object.entries(valueMap).map<IUpdatedFilter>(([key, item]) => ({
      filterKey: key,
      value: item.value,
      updatedValueDisplayName: item.valueDisplayName,
      cachedDynamicEntitiesMap: {},
    }));
    this.updateReportFilter(...updatedFilters);
  };

  private processInsightsCalculation = (insights: ICollection<IInsight>, insightIds: string): IPromise<unknown> => {
    this.$scope.insightsIdMap = insights.items.reduce((idMap: IInsightsIdMap, currentInsight: IInsight): IInsightsIdMap => {
      idMap[currentInsight.id] = {
        insight: currentInsight,
        loading: currentInsight.state.status === "connected" || currentInsight.source.sourceApplication !== "marketplace" ? true : false,
        commentsCount: 0,
        metrics: [],
        insightRequiredEntities: this.$scope.insightboard.insights.filter((insight) => insight.insightId === currentInsight.id)[0].insightRequiredEntities,
      };

      return idMap;
    }, this.$scope.insightsIdMap);

    let calculateInsightboard: IPromise<IInsightboardTransactions[]>;

    // If there are parameters/filters => then skip the insightboard calculation request (it'll be calculated onFilterChanged function)
    if (this.$scope.insightboard.parameters && this.$scope.insightboard.parameters.length > 0) {
      if (this.$scope.insightboard.isReport && this.$scope.hasExistingSessionInStateParams) {
        this.calculateReportWithSessionFilter();
      }
      calculateInsightboard = this.$q.resolve([]);
    } else {
      // insightboard filters are taken from backend db on calculate request => no need to be sent with request
      calculateInsightboard = this.calculateInsightboardWidgets(this.$scope.insightboard.id).then((col) => col.items);
    }
    const processTransactions = calculateInsightboard.then((transactions) => this.processTransactions(transactions));

    const promiseMap = {
      commentsStats: this.commentService.getCommentsStats(insightIds),
      metricUsages: this.insightService.getInsightsUsagesInMetrics(insightIds),
      _: processTransactions,
      __: this.insightDependencies.loaded,
    };
    return this.$q.all(promiseMap).then((results) => {
      this.$scope.indicators.insightboard = false;
      this.processCommentsStats(results.commentsStats);
      this.processMetricUsages(results.metricUsages);
    });
  };

  private setInsightboardPropertiesDependingOnFlag(
    insightboard: IInsightboard,
    flag: boolean,
    insightTitle: string,
    neighbourInsightTitle?: string,
    newPosition?: number
  ) {
    if (!flag) {
      const neighbourInsight = insightboard?.insights.find((insight) => insight?.insightTitle.toLowerCase().includes(neighbourInsightTitle.toLowerCase()));
      if (neighbourInsight) {
        neighbourInsight.sizeX = newPosition;
      }

      insightboard.insights = insightboard?.insights.filter((insight) => !insight?.insightTitle.toLowerCase().includes(insightTitle.toLowerCase()));
    }
  }

  private calculateReportWithSessionFilter() {
    const sessionFilter: IInsightboardItemSetting = {
      name: this.strings.session,
      value: this.$stateParams.sessionId,
    };

    const teamFilter: IInsightboardItemSetting = {
      name: this.strings.team,
      value: "",
    };

    this.calculateInsightboard(this.$scope.insightboard.id, [sessionFilter, teamFilter]);
  }

  private processTransactions = (transactions: IInsightboardTransactions[]) => {
    this.$scope.insightsLoaded = true;

    transactions?.forEach((transaction): void => {
      this.transactionsMap[transaction.transactionId] = transaction.insightId;

      const currentInsight = this.$scope.insightsIdMap[transaction.insightId];

      if (currentInsight) {
        currentInsight.loading = this.checkInsightStatusConnected(currentInsight) || this.checkInsightSourceApplication(currentInsight) ? true : false;
      }
    });

    this.processUnhandledResponses();
  };

  private processUnhandledResponses() {
    const currentUnhandledResponses = [];
    for (const response of this.unhandledResponses) {
      if (!this.updateInsightResult(response)) {
        currentUnhandledResponses.push(response);
      }
    }

    // Remove only unhandled responses from the current iteration instead of all unhandled responses
    this.unhandledResponses = currentUnhandledResponses;
  }

  private processCommentsStats = (commentsStatsMap: ICommentStatsMap): void => {
    for (const id in commentsStatsMap) {
      if (Object.prototype.hasOwnProperty.call(commentsStatsMap, id) && this.$scope.insightsIdMap[id]) {
        this.$scope.insightsIdMap[id].commentsCount = commentsStatsMap[id].count;
      }
    }
  };

  private processMetricUsages = (metricUsagesMap: IInsightMetricUsageMap): void => {
    for (const insightId in metricUsagesMap) {
      if (Object.prototype.hasOwnProperty.call(metricUsagesMap, insightId) && this.$scope.insightsIdMap[insightId]) {
        this.$scope.insightsIdMap[insightId].metrics = metricUsagesMap[insightId];
      }
    }
  };

  private configureGrid = (): void => {
    let columns = 6;

    if (window.innerWidth < 900) {
      columns = 4;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;

    this.$scope.gridsterOptions = {
      columns: columns, // the width of the grid, in columns
      pushing: true, // whether to push other items out of the way on move or resize
      // whether to automatically float items up so they stack (you can temporarily disable if
      // you are adding unsorted items with ng-repeat)
      floating: true,
      // whether or not to have items of the same size switch places instead of pushing
      // down if they are the same size
      swapping: true,
      width: "auto", // can be an integer or 'auto'. 'auto' scales gridster to be the full width of its containing element
      colWidth: "auto", // can be an integer or 'match'.  Match uses the colWidth, giving you square widgets.
      margins: [30, 30], // the pixel distance between each widget
      outerMargin: true, // whether margins apply to outer edges of the grid
      isMobile: false,
      rowHeight: "100", // stacks the grid items if true
      mobileBreakPoint: 600, // if the screen is not wider that this, remove the grid layout and stack the items
      mobileModeEnabled: true, // whether or not to toggle mobile mode when screen width is less than mobileBreakPoint
      minRows: 1, // the minimum height of the grid, in rows
      resizable: {
        enabled: false,
      },
      draggable: {
        enabled: false,
        drag: function (event: IGridEvent) {
          const scrollSensitivity = 60;
          const scrollSpeed = 15;

          if (event.pageY - window.pageYOffset < scrollSensitivity) {
            window.scroll({ top: window.scrollY - scrollSpeed });
          } else if (window.innerHeight - event.pageY < scrollSensitivity) {
            window.scroll({ top: window.scrollY + scrollSpeed });
          }
        },
        stop: function () {
          ctrl.reorderInvisibleWidgets();
        },
      },
    };
  };

  private getDataFlowItems = (): void => {
    const dataFlowRequest: IGetDataFlowParams = {
      fields: this.dataFlowFields,
      includeEntities: true,
    };
    this.dataSourceService.getDataflow(dataFlowRequest).then(
      (dataFlowItems) => {
        dataFlowItems.forEach((blueprint) => {
          blueprint.connections.forEach((connection) => {
            connection.dataSources.forEach((dataSource) => {
              this.$scope.dataFlowBlueprints[dataSource.dataSource.id] = blueprint.blueprint;
              this.$scope.dataFlowDataSources[dataSource.dataSource.id] = dataSource;
            });
          });
        });
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "loading", inline: true })
    );
  };

  private toggleCastMode = (): void => {
    this.$rootScope.cast = !this.$rootScope.cast;
    this.$rootScope.noNavigation = !this.$rootScope.noNavigation;
  };

  private configureHotkeys = (): void => {
    // keyboard stroke for enabling cast capabilitues
    this.$rootScope.cast = false;
    this.hotkeys.add({
      combo: "m",
      callback: () => this.toggleCastMode(),
    });

    // keyboard stroke for copying insightboard's masterplan
    this.hotkeys.add({
      combo: "ctrl+m",
      callback: () => {
        this.copyMasterplan();
      },
    });

    // keyboard stroke that redirects user to edit an insight
    this.hotkeys.add({
      combo: "ctrl+e",
      callback: () => {
        if (this.$scope.focusedInsight !== "") {
          this.$state.go("gtmhub.insight", { name: this.$scope.focusedInsight, dashboardId: this.$scope.insightboard.id });
        }
      },
    });

    this.hotkeys.add({
      combo: "e",
      callback: () => {
        if (!this.$scope.allowedActionsMap[this.strings.update] || this.$scope.insightboard.readOnly) {
          return;
        }

        if (this.$scope.mode === "edit") {
          this.exitEditMode(true);
        } else {
          this.enterEditMode();
        }
      },
    });

    this.hotkeys.add({
      combo: "c",
      callback: () => {
        if (!this.$rootScope.hasPermission("ManageData")) {
          return;
        }

        this.createInsight(true);
      },
    });

    this.hotkeys.add({
      combo: "d",
      callback: () => {
        if (
          (!this.$scope.allowedActionsMap[this.strings.update] && !this.$scope.allowedActionsMap[this.strings.modifyPermissions]) ||
          this.$scope.insightboard.readOnly
        ) {
          return;
        }

        this.$state.go("._edit.edit", { editDashboardId: this.$scope.insightboard.id });
      },
    });

    this.hotkeys.add({
      combo: "t",
      callback: () => {
        if (!this.$scope.allowedActionsMap[this.strings.update] || this.$scope.insightboard.readOnly) {
          return;
        }

        this.$state.go("._edit.tags", { tagsDashboardId: this.$scope.insightboard.id });
      },
    });

    this.hotkeys.add({
      combo: "s",
      callback: () => {
        if (!this.$scope.allowedActionsMap[this.strings.update] || this.$scope.insightboard.readOnly) {
          return;
        }

        if (this.$scope.mode === "edit" && this.$state.current.name !== "gtmhub.insightboard.widgets.browser") {
          this.processChanges();
        }
      },
    });

    this.hotkeys.add({
      combo: "a",
      callback: () => {
        if (!this.$scope.allowedActionsMap[this.strings.update] || !this.$rootScope.hasPermission("ManageData") || this.$scope.insightboard.readOnly) {
          return;
        }

        if (this.$state.current.name === "gtmhub.insightboard") {
          this.$state.go(".widgets.browser", { widgetsDashboardId: this.$scope.insightboard.id });
        }

        if (this.$scope.mode !== "edit") {
          this.enterEditMode();
        }
      },
    });
  };

  private onInsightResponse = (ev: IAngularEvent, response: IInsightResponse): void => {
    if (this.transactionsMap[response.transactionID]) {
      this.updateInsightResult(response);
    } else {
      this.unhandledResponses.push(response);
    }
  };

  private updateInsightResult = (response: IInsightResponse): boolean => {
    if (this.$scope.insightsIdMap && this.transactionsMap[response.transactionID] && this.$scope.insightsIdMap[this.transactionsMap[response.transactionID]]) {
      if (++this.insightBoardInsightConnections === this.$scope.insightboard.insights.length) {
        this.$scope.allSocketsReceived = true;
      }

      if (this.isInsightRenderingOptimisationsEnabled) {
        if (this.updateInsightResultBuffer.length === 0) {
          this.$timeout(() => {
            this.updateInsightResultBuffer.forEach((bufferedResponse) => {
              this.$scope.insightsIdMap[this.transactionsMap[bufferedResponse.transactionID]].calculationResult = bufferedResponse.data;
              this.$scope.insightsIdMap[this.transactionsMap[bufferedResponse.transactionID]].loading = false;
              delete this.transactionsMap[response.transactionID];
            });
            this.updateInsightResultBuffer = [];
          }, 500);
        }
        this.updateInsightResultBuffer.push(response);
      } else {
        this.$scope.insightsIdMap[this.transactionsMap[response.transactionID]].calculationResult = response.data;
        this.$scope.insightsIdMap[this.transactionsMap[response.transactionID]].loading = false;
        delete this.transactionsMap[response.transactionID];
      }

      return true;
    }

    return false;
  };

  private deepCopyWidgets = (widgets: IInsightboardItem[]): IInsightboardWidgets => {
    const widgetsCollection: IInsightboardWidgets = {
      array: [],
      map: {},
    };

    return widgets.reduce((collection: IInsightboardWidgets, currentWidget: IInsightboardItem): IInsightboardWidgets => {
      const copiedWidget: IInsightboardItem = angular.copy(currentWidget);

      collection.array.push(copiedWidget);
      collection.map[copiedWidget.id] = copiedWidget;

      return collection;
    }, widgetsCollection);
  };

  private getFilterSettings = (): IInsightboardItemSetting[] => {
    if (this.$scope.insightboard.isReport) {
      return this.reportFilter.dashboardItemsSettings;
    }

    return this.$scope.insightboard.settings;
  };

  private processParameters = (): void => {
    let settings = this.getFilterSettings();
    this.$rootScope.filters = {};

    // these are the filters' info coming from account settings/insightboard parameters and that are used in the
    // particular insightboard and its insights
    const filters = this.$scope.insightboard.parameters;

    for (const filter of filters) {
      this.$rootScope.filters[filter.key] = filter.title;
    }

    this.$scope.selectedParameters = {};

    if (typeof settings === "undefined" || settings === null) {
      settings = [];
    }

    this.initGridFilters(this.$scope.insightboard, this.reportFilter);
  };

  private onInsightboardTagsChanged = (ev: IAngularEvent, board: IInsightboard): void => {
    if (this.$scope.insightboard.id === board.id) {
      this.$scope.insightboard.tags = board.tags;
    }
  };

  private onInsightboardEdited = (ev: IAngularEvent, editedInsightboard: IInsightboard): void => {
    if (this.$scope.insightboard.id === editedInsightboard.id) {
      this.$scope.insightboard.name = editedInsightboard.name;
    }
  };

  private saveInsightboardItemsAndEditWidget = (widget: IInsightboardItem): void => {
    this.processChanges(false, widget);
  };

  private editWidget = (widget: IInsightboardItem): void => {
    const options = {
      name: widget.insightName,
      dashboardId: this.$scope.insightboard.id,
    };

    if (widget.formatting.isCodeless) {
      this.$state.go(this.strings.insightCodelessEditorState, options);
      return;
    }

    this.$state.go(this.strings.insightState, options);
  };

  private sortWidgets = (widgets: NodeListOf<HTMLElement>): HTMLElement[] => {
    const enrichedWidgets: IEnrichedWidget[] = [];

    for (let i = 0; i < widgets.length; i++) {
      const styles = window.getComputedStyle(widgets[i]);
      const top = styles.top;
      const left = styles.left;

      const widget = {
        widget: widgets[i],
        top: top ? Number(top.substr(0, top.length - 2)) : null,
        left: left ? Number(left.substr(0, left.length - 2)) : null,
      };

      if (widget.top && widget.left) {
        enrichedWidgets.push(widget);
      }
    }

    enrichedWidgets.sort(function (a, b) {
      return a.top - b.top || a.left - b.left;
    });

    return enrichedWidgets.map((w) => w.widget);
  };

  private addStyleToWidgetsAndReturnHtml = (widgets: HTMLElement[]): string => {
    let widgetsHtml = "";
    for (let i = 0; i < widgets.length; i++) {
      const styles = window.getComputedStyle(widgets[i]);
      const width = styles.width;
      const style = width ? `width: ${width};` : "";
      this.loadImageSrc(widgets[i], "src");
      this.loadImageSrc(widgets[i], "lazy-src");

      let widgetHtml = "";
      if (widgets[i].classList.contains("section-header")) {
        const sectionHeaderTitle = document.querySelector<HTMLInputElement>(
          `.section-header[data-widget-id='${widgets[i].dataset["widgetId"]}'] .section-header-title`
        ).value;
        const sectionHeaderSubTitle = document.querySelector<HTMLInputElement>(
          `.section-header[data-widget-id='${widgets[i].dataset["widgetId"]}'] .section-header-sub-title`
        ).value;

        widgetHtml = `<li class="widget gridster-item section-header" style="${style}"><div class="${widgets[i].firstElementChild.className}"><div class="widget-template"><div class="content"><input class="section-header-title" value="${sectionHeaderTitle}"><input class="section-header-sub-title" value="${sectionHeaderSubTitle}"></div></div></div></li>`;
      } else {
        widgetHtml = `<li class="widget gridster-item" style="${style}">${widgets[i].innerHTML}</li>`;
      }

      widgetsHtml += widgetHtml;
    }

    return widgetsHtml;
  };

  private loadImageSrc = (widget: HTMLElement, attrSelector: "src" | "lazy-src"): void => {
    const images = widget.querySelectorAll(`img[${attrSelector}]`);
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const imageSrc = image.getAttribute(attrSelector);
      const decodedImageSrc = decodeURIComponent(imageSrc);
      // directly pass the avatar url from the query params of the gravatar redirect req
      if (decodedImageSrc.includes("gravatar")) {
        const auth0Avatar = decodedImageSrc.split("d=")[1];
        image.setAttribute("src", auth0Avatar);
      } else if (attrSelector === "lazy-src") {
        // load the lazy-src avatar
        image.setAttribute("src", imageSrc);
      }
    }
  };

  private exportToPdf = (): void => {
    this.$scope.indicators.exporting = true;

    const titleEl = document.querySelector("title");
    const savedHtmlTitle = titleEl.innerText;
    titleEl.innerText = this.$scope.insightboard.name;

    // get the widgets from the gridster's list
    const widgets = document.querySelectorAll<HTMLElement>(".gridster > ul > .widget");

    const sortedWidgets = this.sortWidgets(widgets);

    const widgetsHtml: string = this.addStyleToWidgetsAndReturnHtml(sortedWidgets);

    // Construct a whole html doc with required style file in head and widgets in body
    // Note: Backend needs to know the style file's name to apply styles in backend => sent in the doc's head
    const widgetsHtmlDoc = `<!doctype html>
    <html lang="en">
      <head>
        <link rel="stylesheet" href="style.css" type="text/css">
      </head>
      <body>
        <ul>${widgetsHtml}</ul>
      </body>
    </html>`;

    const query: IInsightBoardPdfQuery = {
      sourceType: "dashboard", // necessary for tracking right segment data since the pdf exporter will be used in whole webapp
      properties: {
        insightboardId: this.$scope.insightboard.id,
        insightsCount: this.$scope.insightboard.insightBoardItems.filter((item) => item.type !== "sectionHeader").length,
      },
      payload: this.documentExporterService.minifyAndEncodeHtml(widgetsHtmlDoc),
    };

    if (this.$scope.isImprovePdfExportsEnabled) {
      this.$scope.indicators.exporting = false;
      this.openPdfPreview(query, savedHtmlTitle);
    } else {
      this.documentExporterService
        .exportPdf(query)
        .then((pdfStringResponse) => {
          document.querySelector("title").innerText = savedHtmlTitle;
          this.$scope.indicators.exporting = false;
          const generatedPdf = new Blob([pdfStringResponse], { type: "application/pdf" });
          triggerFileDownload(generatedPdf, this.$scope.insightboard.name + "_" + new Date().toLocaleDateString() + ".pdf");
        })
        .catch((error) => this.uiErrorHandlingService.handleModal(error));
    }
  };

  private openPdfPreview = (query: IInsightBoardPdfQuery, title: string) => {
    this.$uibModal.open({
      component: "pdfPreview",
      resolve: {
        query: () => query,
        title: () => title,
      },
      windowClass: "medium",
    });
  };

  private deleteWidget = (index: number, widget: IInsightboardItem): void => {
    if (widget.type !== "sectionHeader") {
      this.$scope.insightboard.insightBoardItems = this.$scope.insightboard.insightBoardItems.filter((insight, insightIndex) => index !== insightIndex);

      widget.filters.forEach((filterKey) => {
        if (this.insightsParametersUsageMap[filterKey]) {
          const lastIndex = this.insightsParametersUsageMap[filterKey].lastIndexOf(widget.insightId);
          if (lastIndex !== -1) {
            this.insightsParametersUsageMap[filterKey].splice(lastIndex, 1);
          }
        }
      });
    } else {
      this.removeSectionHeaderWidget(index, widget);
    }
    this.$scope.limit--;
    this.reorderInvisibleWidgets();
  };

  private removeSectionHeaderWidget = (index: number, widget: IInsightboardItem): void => {
    if (widget.sectionHeader.header.trim() !== "" || widget.sectionHeader.subHeader.trim() !== "") {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const ctrl = this;
      this.$uibModal.open({
        templateUrl: require("../views/confirm-delete-section-header-widget.jade"),
        windowClass: "medium-small confirm-delete-section-header-widget",
        controller: [
          "$scope",
          "$uibModalInstance",
          function ($scope, $uibModalInstance) {
            $scope.close = () => {
              $uibModalInstance.close();
            };
            $scope.delete = () => {
              $uibModalInstance.close();
              ctrl.$scope.insightboard.insightBoardItems = ctrl.$scope.insightboard.insightBoardItems.filter((insight, insightIndex) => index !== insightIndex);
            };
          },
        ],
      });
    } else {
      this.$scope.insightboard.insightBoardItems = this.$scope.insightboard.insightBoardItems.filter((insight, insightIndex) => index !== insightIndex);
    }
  };

  private cloneInsight = (widget: IInsightboardItem) => {
    this.insightService
      .getInsight(widget.insightName)
      .then((insight) => {
        insight.name = insight.name + "-" + Math.random().toString(36).substr(2, 5);
        insight.title = insight.title + " cloning";

        if (!insight.language) {
          insight.language = "r";
        }

        return this.insightService.createInsight(insight, { cloned: true }).then((newInsight) => {
          this.onWidgetsAdded([newInsight]);

          setTimeout(() => {
            document.getElementById(newInsight.name).scrollIntoView();
          }, 0);
        });
      })
      .catch((err) => this.uiErrorHandlingService.handleModal(err));
  };

  private onWidgetsAdded = (insights: IInsight[]): void => {
    const insightIdsArr: string[] = [];
    insights.forEach((insight: IInsight, index): void => {
      insightIdsArr.push(insight.id);
      const newWidget: IInsightboardItemView = {
        dashboardId: this.$scope.insightboard.id,
        sizeX: insight.size.width,
        sizeY: insight.size.height,
        insightName: insight.name,
        insightId: insight.id,
        type: "insight",
        filters: angular.copy(insight.usedFilters),
        formatting: angular.copy(insight.formatting),
        insightState: angular.copy(insight.state),
        insightSource: angular.copy(insight.source),
      };

      newWidget.shouldHightlight = true;
      const isLastItem = index === insights.length - 1;
      if (isLastItem) {
        newWidget.scrollIntoView = true;
      }

      this.$scope.insightboard.insightBoardItems.push(newWidget);
      this.$scope.limit = this.$scope.insightboard.insightBoardItems.length;

      if (!this.$scope.insightsIdMap[insight.id]) {
        this.$scope.insightsIdMap[insight.id] = {
          insight: insight,
          commentsCount: 0,
        };
      }

      insight.usedFilters.forEach((filterKey: string): void => {
        if (!this.insightsParametersUsageMap[filterKey]) {
          this.insightsParametersUsageMap[filterKey] = [];
        }

        this.insightsParametersUsageMap[filterKey].push(insight.id);
      });

      const currentInsight = this.$scope.insightsIdMap[insight.id];

      if (!currentInsight.calculationResult) {
        currentInsight.loading = this.checkInsightStatusConnected(currentInsight) || this.checkInsightSourceApplication(currentInsight) ? true : false;

        const parameters: IInsightboardItemSetting[] = this.generateCalculationParams(insight, this.insightsParametersUsageMap);

        // Make sure insights socket is open before calculating insights data
        this.messagingService.onInsightsSocketOpen().then(() => this.requestInsightCalculationById(insight.id, parameters));
      }
    });

    const insightIds: string = insightIdsArr.join(",");

    const promiseMap = {
      commentsStats: this.commentService.getCommentsStats(insightIds),
      metricUsages: this.insightService.getInsightsUsagesInMetrics(insightIds),
    };
    this.$q.all(promiseMap).then(
      (resolvedMap) => {
        this.processCommentsStats(resolvedMap.commentsStats);
        this.processMetricUsages(resolvedMap.metricUsages);
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private checkInsightStatusConnected = (currentInsight: IInsightDetails): boolean => {
    if (currentInsight.insight && currentInsight.insight.state && currentInsight.insight.state.status) {
      return currentInsight.insight.state.status === "connected";
    }
    return false;
  };

  private checkInsightSourceApplication = (currentInsight: IInsightDetails): boolean => {
    if (currentInsight.insight && currentInsight.insight.source && currentInsight.insight.source.sourceApplication) {
      return currentInsight.insight.source.sourceApplication !== "marketplace";
    }
    return false;
  };

  private generateCalculationParams = (insight: IInsight, filtersMap: IInsightboardFiltersUsage): IInsightboardItemSetting[] => {
    if (!insight || !insight.usedFilters || insight.usedFilters.length === 0) {
      return null;
    }

    const parameters: IInsightboardItemSetting[] = [];

    insight.usedFilters.forEach((filterKey: string): void => {
      if (!filtersMap[filterKey]) {
        filtersMap[filterKey] = [];
      }

      filtersMap[filterKey].push(insight.id);

      let filterSetting: IInsightboardItemSetting;
      const gridFilter = this.$scope.filters && this.$scope.filters.length && this.$scope.filters.find((f) => f.fieldName === filterKey);

      if (gridFilter) {
        filterSetting = createInsightboardSettingFromGridFilter(gridFilter);
      } else {
        filterSetting = {
          name: filterKey,
          value: "",
        };
      }
      parameters.push(filterSetting);
    });

    return parameters;
  };

  private onStateChanged = (trans: Transition) => {
    this.$scope.$watch("insightboard", () => {
      if (!this.$scope.insightboard) {
        return;
      }
      const params = trans.router.globals.params;

      const widget: IInsight = params.widget;
      const widgets: IInsight[] = params.widgets;

      if (widget) {
        this.enterEditMode();
        this.onWidgetsAdded([widget]);
      }

      if (widgets) {
        this.enterEditMode();
        this.onWidgetsAdded(widgets);
      }
    });
  };
  private enterEditMode = () => {
    this.$scope.mode = "edit";
    this.$scope.$broadcast("insightboardModeChanged", "edit");

    this.$scope.gridsterOptions.pushing = true;
    this.$scope.gridsterOptions.floating = true;
    this.$scope.gridsterOptions.swapping = true;
    this.$scope.gridsterOptions.resizable = {
      enabled: true,
    };

    this.$scope.gridsterOptions.draggable = {
      enabled: true,
    };

    document.body.classList.add("edit-background");
    window.addEventListener("scroll", this.stickyInsightActions);
  };

  private processChanges = (exitEditMode = false, widgetToBeEdited?: IInsightboardItem) => {
    this.analyticsService.track("Button Clicked", this.getButtonEventMetaInformation("insightboard_navigation_save_changes"));

    const sectionHeaderItems = this.$scope.insightboard.insightBoardItems.filter((item) => item.type === "sectionHeader");
    const initialSectionHeaderItems = this.initialWidgets.array.filter((item) => item.type === "sectionHeader");

    if (sectionHeaderItems.length === 0 && initialSectionHeaderItems.length === 0) {
      this.saveBoardChanges(exitEditMode, widgetToBeEdited);
    } else {
      const deleteSectionHeaderssPromises = this.deleteSectionHeaders(initialSectionHeaderItems, sectionHeaderItems);
      const createdSectionHeadersPromises = this.createSectionHeaders(initialSectionHeaderItems, sectionHeaderItems);
      const updatedSectionHeadersPromises = this.updateSectionHeaders(initialSectionHeaderItems, sectionHeaderItems);
      const promises = [...deleteSectionHeaderssPromises, ...createdSectionHeadersPromises, ...updatedSectionHeadersPromises];

      this.$q.all(promises).then(() => {
        this.saveBoardChanges(exitEditMode, widgetToBeEdited);
      });
    }
  };

  private saveBoardChanges = (exitEditMode = false, widgetToBeEdited?: IInsightboardItem) => {
    this.exitEditMode(exitEditMode);

    this.insightboardService.saveInsightboardItems(this.$scope.insightboard.id, this.$scope.insightboard.insightBoardItems).then(
      (insightBoardItemResponse) => {
        const widgets = sortInsightBoardItems([...insightBoardItemResponse.sectionHeaders, ...insightBoardItemResponse.insights]);
        const addedParameters = this.findAddedParameters();
        if (addedParameters.length > 0) {
          this.onInsightboardParameterAdded(addedParameters, insightBoardItemResponse);

          if (widgetToBeEdited) {
            this.editWidget(widgetToBeEdited);
          }

          this.showSavedChangesMessage();
          return;
        }

        const removedParameters = this.findRemovedParameters();
        if (addedParameters.length === 0 && removedParameters) {
          this.$scope.insightboard.parameters = this.$scope.insightboard.parameters.filter((parameter) => !removedParameters[parameter.key]);
          this.$scope.insightboard.settings = this.$scope.insightboard.settings.filter((setting) => !removedParameters[setting.name]);
          this.processParameters();

          this.initialWidgets = this.deepCopyWidgets(widgets);
          this.insightsParametersUsageMap = this.resetInsightsParametersUsageMap(insightBoardItemResponse.insights);

          if (widgetToBeEdited) {
            this.editWidget(widgetToBeEdited);
          }

          this.saveFilters();
          this.showSavedChangesMessage();
          return;
        }

        this.initialWidgets = this.deepCopyWidgets(widgets);
        this.insightsParametersUsageMap = this.resetInsightsParametersUsageMap(insightBoardItemResponse.insights);

        if (widgetToBeEdited) {
          this.editWidget(widgetToBeEdited);
        }

        this.showSavedChangesMessage();
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private showSavedChangesMessage = () => {
    this.$scope.showChangesSavedMessage = true;

    this.$timeout(() => {
      this.$scope.showChangesSavedMessage = false;
    }, 1500);
  };

  private stickyInsightActions = () => {
    const element: HTMLElement = document.querySelector(".insightboard-actions");
    const currentScrollTop = window.scrollY;

    // stick the element on top when 2/3 of its own height is not in view
    if (currentScrollTop > element.clientHeight * 0.66) {
      element.classList.add("sticky-element");
    } else {
      element.classList.remove("sticky-element");
    }
  };

  private addSectionHeader = () => {
    if (this.$scope.mode != "edit") {
      this.enterEditMode();
    }

    const row = Math.min(Math.ceil(window.scrollY / 99));

    const newSectionHeaderItem: IInsightboardItemView = {
      dashboardId: this.$scope.insightboard.id,
      sizeX: 6,
      sizeY: 1,
      row: row,
      col: 0,
      type: "sectionHeader",
      id: Math.random().toString(36).substr(2, 10),
      sectionHeader: {
        header: "",
        subHeader: "",
      },
    };

    this.$scope.insightboard.insightBoardItems.push(newSectionHeaderItem);

    if (this.$scope.insightboard.insightBoardItems.length > this.$scope.limit) {
      this.$scope.limit++;
    }

    sortInsightBoardItems(this.$scope.insightboard.insightBoardItems);

    this.$timeout(() => {
      this.reorderInvisibleWidgets();
    }, 0);

    setTimeout(() => {
      const widget = document.querySelector<HTMLElement>(".widget[data-widget-id='" + newSectionHeaderItem.id + "']");
      const top = Number.parseInt(widget.style.top.replace("px", ""));
      window.scrollTo({ top: top - 60 });
      document.querySelector<HTMLElement>(".widget[data-widget-id='" + newSectionHeaderItem.id + "'] .section-header-title").focus();

      this.analyticsService.track("Button Clicked", this.getButtonEventMetaInformation("insightboard_navigation_add"));
    }, 400);
  };

  private removeBorderOnFocus = (widgetSelector: string) => {
    if (this.$scope.mode === "edit") {
      document.querySelector(".widget[data-widget-id='" + widgetSelector + "']").classList.add("on-focus");
    }
  };

  private revertBorderOnBlur = ($event, widgetSelector: string) => {
    if (this.$scope.mode === "edit") {
      this.$timeout(() => {
        const activeElement = document.activeElement;

        if (activeElement === $event.target.nextElementSibling || activeElement === $event.target.previousElementSibling) {
          return;
        } else if ($event.target.parentElement.firstChild.value.trim() == "") {
          const index = Number.parseInt($event.target.parentElement.dataset["index"]);
          this.$scope.insightboard.insightBoardItems = this.$scope.insightboard.insightBoardItems.filter((insight, insightIndex) => index !== insightIndex);
          this.$scope.limit--;
        } else {
          document.querySelector(".widget[data-widget-id='" + widgetSelector + "']").classList.remove("on-focus");
        }
      }, 1);
    }
  };

  private setInputWidthForSectionHeaderTitle = (text: string) => {
    return text.length > 12 ? `${text.length * 1.1}ch` : "20ch";
  };

  private setInputWidthForSectionHeaderSubTitle = (text: string) => {
    return text.length > 16 ? `${text.length * 1.1}ch` : "20ch";
  };

  private onKeyDownSectionHeader = ($event) => {
    // ON TAB
    if ($event.keyCode == 9) {
      $event.preventDefault();
      if ($event.currentTarget.classList.contains("section-header-title")) {
        $event.currentTarget.nextSibling.focus();
      } else {
        $event.currentTarget.previousSibling.focus();
      }
      // ON ENTER
    } else if ($event.keyCode == 13) {
      if ($event.currentTarget.parentElement.firstChild.value.trim() == "") {
        const index = Number.parseInt($event.currentTarget.parentElement.dataset["index"]);
        this.$scope.insightboard.insightBoardItems = this.$scope.insightboard.insightBoardItems.filter((insight, insightIndex) => index !== insightIndex);
        this.$scope.limit--;
      } else {
        $event.currentTarget.blur();
      }
    }
  };

  private createSectionHeaders = (initialSectionHeaderItems: IInsightboardItem[], sectionHeaderItems: IInsightboardItem[]): IPromise<void>[] => {
    const createdSectionHeadersPromises = [];

    const sectionHeaderItemsToCreate = sectionHeaderItems.filter(
      (sectionHeaderItem) => !initialSectionHeaderItems.some((initialSectionHeaderItem) => initialSectionHeaderItem.sectionHeaderId == sectionHeaderItem.sectionHeaderId)
    );

    sectionHeaderItemsToCreate.forEach((sectionHeaderItemToCreate) => {
      const createdPromise = this.sectionHeaderService
        .createSectionHeader({
          header: sectionHeaderItemToCreate.sectionHeader.header,
          subHeader: sectionHeaderItemToCreate.sectionHeader.subHeader,
        })
        .then(
          (createdSectionHeader) => {
            sectionHeaderItemToCreate.sectionHeaderId = createdSectionHeader.id;
            sectionHeaderItemToCreate.sectionHeader.id = createdSectionHeader.id;
            this.analyticsService.track("Insightboard Section Header Created", { has_subheading: createdSectionHeader.subHeader.length > 0 });
          },
          (err) => this.uiErrorHandlingService.handleModal(err)
        );

      createdSectionHeadersPromises.push(createdPromise);
    });

    return createdSectionHeadersPromises;
  };

  private deleteSectionHeaders = (initialSectionHeaderItems: IInsightboardItem[], sectionHeaderItems: IInsightboardItem[]): IPromise<void>[] => {
    const deletedSectionHeadersPromises = [];

    const sectionHeaderItemsToDelete = initialSectionHeaderItems.filter(
      (initialSectionHeaderItem) => !sectionHeaderItems.some((sectionHeaderItem) => sectionHeaderItem.sectionHeaderId == initialSectionHeaderItem.sectionHeaderId)
    );

    sectionHeaderItemsToDelete.forEach((sectionHeaderItemToDelete) => {
      const deletedPromise = this.sectionHeaderService.deleteSectionHeader(sectionHeaderItemToDelete.sectionHeaderId).then(
        () => {
          this.analyticsService.track("Insightboard Section Header Deleted", { has_subheading: sectionHeaderItemToDelete.sectionHeader.subHeader.length > 0 });
        },
        (err) => this.uiErrorHandlingService.handleModal(err)
      );

      deletedSectionHeadersPromises.push(deletedPromise);
    });

    return deletedSectionHeadersPromises;
  };

  private updateSectionHeaders = (initialSectionHeaderItems: IInsightboardItem[], sectionHeaderItems: IInsightboardItem[]): IPromise<void>[] => {
    const updatedSectionHeadersPromises = [];

    const sectionHeaderItemsForUpdate = sectionHeaderItems.filter((sectionHeaderItem) =>
      initialSectionHeaderItems.some((initialSectionHeaderItem) => initialSectionHeaderItem.sectionHeaderId == sectionHeaderItem.sectionHeaderId)
    );

    sectionHeaderItemsForUpdate.forEach((sectionHeaderItemForUpdate) => {
      const initialSectionHeader = this.initialWidgets.array.find((initialItems) => initialItems.sectionHeaderId === sectionHeaderItemForUpdate.sectionHeaderId);
      const sectionHeaderForUpdate = sectionHeaderItemForUpdate.sectionHeader;

      if (
        initialSectionHeader.sectionHeader.header !== sectionHeaderForUpdate.header ||
        initialSectionHeader.sectionHeader.subHeader !== sectionHeaderForUpdate.subHeader
      ) {
        const updatedPromise = this.sectionHeaderService
          .updateSectionHeader({
            id: sectionHeaderForUpdate.id,
            header: sectionHeaderForUpdate.header,
            subHeader: sectionHeaderForUpdate.subHeader,
          })
          .then(
            (updatedSectionHeader) => {
              if (initialSectionHeader.sectionHeader.subHeader.length > 0 && updatedSectionHeader.subHeader.length === 0) {
                this.analyticsService.track("Insightboard Section SubHeader Removed");
              } else if (
                initialSectionHeader.sectionHeader.subHeader.length == 0 &&
                updatedSectionHeader.subHeader.length >= 1 &&
                initialSectionHeader.sectionHeader.subHeader != updatedSectionHeader.subHeader
              ) {
                this.analyticsService.track("Insightboard Section SubHeader Added");
              }
            },
            (err) => this.uiErrorHandlingService.handleModal(err)
          );

        updatedSectionHeadersPromises.push(updatedPromise);
      }
    });

    return updatedSectionHeadersPromises;
  };

  private cancelEdit = () => {
    this.analyticsService.track("Button Clicked", this.getButtonEventMetaInformation("insightboard_navigation_cancel"));
    this.exitEditMode(true);
  };

  private getButtonEventMetaInformation = (buttonName: string) => {
    const stickyHeader = document.querySelector(".insightboard-actions");
    const isStickyHeaderVisible = stickyHeader && stickyHeader.classList.contains("sticky-element");
    const container = document.querySelector(".insightboard");
    const isInsightboardScrollable = container.scrollHeight > window.innerHeight;

    return {
      button_name: buttonName,
      is_sticky_header_visible: isStickyHeaderVisible,
      is_insightboard_scrollable: isInsightboardScrollable,
    };
  };

  private findAddedParameters = (): string[] => {
    const addedParameters: string[] = [];
    for (const filterKey in this.insightsParametersUsageMap) {
      const filters = this.$scope.filters && this.$scope.filters.length ? this.$scope.filters : [];
      if (!filters.find((f) => f.fieldName === filterKey)) {
        addedParameters.push(filterKey);
      }
    }

    return addedParameters;
  };

  private findRemovedParameters = (): IRemovedInsightboardParametersMap => {
    const removedParameters: IRemovedInsightboardParametersMap = {};

    for (const parameterKey in this.insightsParametersUsageMap) {
      if (this.insightsParametersUsageMap[parameterKey].length === 0) {
        removedParameters[parameterKey] = true;
      }
    }

    return Object.keys(removedParameters).length ? removedParameters : null;
  };

  private onInsightboardParameterAdded = (addedParameters: string[], savedWidgets: IInsightBoardItemResponse): void => {
    this.insightboardService
      .getInsightboard(this.$stateParams.dashboardId, this.paramsAndSettingsBoardFields)
      .then((board) => {
        const widgets = sortInsightBoardItems([...savedWidgets.sectionHeaders, ...savedWidgets.insights]);
        this.$scope.insightboard.parameters = board.parameters;
        this.$scope.insightboard.settings = board.settings;

        this.processParameters();
        this.saveFilters();

        let caclulationParameters: IInsightboardItemSetting[] = [];

        caclulationParameters = addedParameters.reduce((params: IInsightboardItemSetting[], currentFilterKey: string): IInsightboardItemSetting[] => {
          const gridFilter = this.$scope.filters.find((f) => f.fieldName === currentFilterKey);

          params.push({
            name: currentFilterKey,
            //we may not have gridFilter for the added parameter if it's no longer existent insightboard parameter
            value: gridFilter ? createInsightboardSettingFromGridFilter(gridFilter).value : "",
          });

          this.setInsightLoading(currentFilterKey);

          return params;
        }, caclulationParameters);

        return this.calculateInsightboardWidgets(board.id, caclulationParameters).then((col) => {
          this.processTransactions(col.items);

          this.initialWidgets = this.deepCopyWidgets(widgets);
          this.insightsParametersUsageMap = this.resetInsightsParametersUsageMap(savedWidgets.insights);
        });
      })
      .catch((err) => this.uiErrorHandlingService.handleModal(err));
  };

  private onCommentPosted = (event: ng.IAngularEvent, comment: IComment): void => {
    if (comment.targetType === "insight" && this.$scope.insightsIdMap[comment.targetId]) {
      this.$scope.insightsIdMap[comment.targetId].commentsCount++;
    }
  };

  private onCommentDeleted = (event: ng.IAngularEvent, comment: IComment): void => {
    if (comment.targetType === "insight" && this.$scope.insightsIdMap[comment.targetId]?.commentsCount > 0) {
      this.$scope.insightsIdMap[comment.targetId].commentsCount--;
    }
  };

  private exitEditMode = (revertToOriginal: boolean) => {
    this.$scope.mode = "view";
    this.$scope.$broadcast("insightboardModeChanged", "view");
    this.$scope.gridsterOptions.resizable.enabled = false;
    this.$scope.gridsterOptions.draggable.enabled = false;

    if (revertToOriginal) {
      this.$scope.insightboard.insightBoardItems = [...this.initialWidgets.array];

      this.insightsParametersUsageMap = this.resetInsightsParametersUsageMap(this.$scope.insightboard.insightBoardItems.filter((item) => item.type !== "sectionHeader"));
    }
    document.body.classList.remove("edit-background");
    window.removeEventListener("scroll", this.stickyInsightActions);
  };

  private resetInsightsParametersUsageMap = (widgets: IInsightboardItem[]): IInsightboardFiltersUsage => {
    const insightsParametersUsageMap: IInsightboardFiltersUsage = {};

    return widgets.reduce((map: IInsightboardFiltersUsage, currentWidget: IInsightboardItem): IInsightboardFiltersUsage => {
      currentWidget.filters.forEach((filterKey: string): void => {
        if (!map[filterKey]) {
          map[filterKey] = [];
        }

        map[filterKey].push(currentWidget.insightId);
      });

      return map;
    }, insightsParametersUsageMap);
  };

  private createInsight = (isCodeless?: boolean) => {
    this.$uibModal.open({
      templateUrl: require("../../insights/views/insight-form.jade"),
      windowClass: "medium-small",
      controller: "CreateInsightCtrl",
      resolve: {
        isCodelessInsight: (): boolean => {
          return isCodeless;
        },
      },
    });
  };

  private focus = (insightTag: string) => {
    this.$scope.focusedInsight = insightTag;
  };

  private markFilledCells = (filledCells: Array<Array<boolean>>, insightBoardItem: IInsightboardItem) => {
    for (let row = insightBoardItem.row; row < insightBoardItem.row + insightBoardItem.sizeY; row++) {
      for (let column = insightBoardItem.col; column < insightBoardItem.col + insightBoardItem.sizeX; column++) {
        // mark cell as taken
        filledCells[row][column] = true;
      }
    }
  };

  private isGridCellTaken = (filledCells: Array<Array<boolean>>, insightBoardItem: IInsightboardItem) => {
    for (let row = insightBoardItem.row; row < insightBoardItem.row + insightBoardItem.sizeY; row++) {
      for (let column = insightBoardItem.col; column < insightBoardItem.col + insightBoardItem.sizeX; column++) {
        if (filledCells[row][column] === true) {
          return true;
        }
      }
    }
    return false;
  };

  private pushOrPullWidgetIfNeeded = (filledCells: Array<Array<boolean>>, insightBoardItem: IInsightboardItem) => {
    while (this.isGridCellTaken(filledCells, insightBoardItem)) {
      insightBoardItem.row++;
    }

    do {
      insightBoardItem.row--;
    } while (!this.isGridCellTaken(filledCells, insightBoardItem));
    // pull it down to the last available row
    insightBoardItem.row++;

    this.markFilledCells(filledCells, insightBoardItem);
  };

  private init2DArray = (rows: number, columns: number): Array<Array<boolean>> => {
    // Create one dimensional array
    const matrix = new Array(rows);

    // Loop to create 2D array using 1D array
    for (let i = 0; i < matrix.length; i++) {
      matrix[i] = Array(...new Array(columns)).map(function () {
        return false;
      });
    }

    return matrix;
  };

  private initVisibleWidgets = (): Array<Array<boolean>> => {
    let LAST_USED_ROW = this.$scope.insightboard.insightBoardItems[this.$scope.insightboard.insightBoardItems.length - 1].row;
    LAST_USED_ROW = LAST_USED_ROW ? LAST_USED_ROW : 0;
    const MAX_POSSIBLE_WIDGET_HEIGHT = 100;
    const GRIDSTER_ROWS = LAST_USED_ROW + MAX_POSSIBLE_WIDGET_HEIGHT;
    const GRIDSTER_COLUMNS = this.$scope.gridsterOptions.columns;

    // by default every cell in the gridster is empty (i.e. false)
    const filledGridCells = this.init2DArray(GRIDSTER_ROWS, GRIDSTER_COLUMNS);

    for (let i = 0; i < this.$scope.insightboard.insightBoardItems.length && i < this.$scope.limit; i++) {
      this.markFilledCells(filledGridCells, this.$scope.insightboard.insightBoardItems[i]);
    }

    return filledGridCells;
  };

  private reorderInvisibleWidgets = (): void => {
    if (this.$scope.insightboard.insightBoardItems.length === 0) {
      return;
    }

    // fill up the matrix with the visible widgets for which we have certain positions
    const filledGridCells = this.initVisibleWidgets();

    for (let i = this.$scope.limit; i < this.$scope.insightboard.insightBoardItems.length; i++) {
      this.pushOrPullWidgetIfNeeded(filledGridCells, this.$scope.insightboard.insightBoardItems[i]);
    }

    sortInsightBoardItems(this.$scope.insightboard.insightBoardItems);
  };

  private loadMoreInsights = (): void => {
    this.reorderInvisibleWidgets();
    this.$scope.limit += LOAD_MORE_INSIGHTS_BY;
  };

  private copyMasterplan = () => {
    const notification = document.createElement("DIV");
    notification.setAttribute("id", "clipboard-notification");
    const notificationString = document.createTextNode("Masterplan copied in your clipboard!");
    notification.appendChild(notificationString);
    document.body.appendChild(notification);
    setTimeout(function () {
      notification.remove();
    }, 1500);

    const element = document.createElement("TEXTAREA");
    element.textContent = this.prepareInsightboardJson();
    document.body.appendChild(element);
    this.selectElementText(element);
    document.execCommand("copy");
    element.remove();
  };

  private selectElementText = (element) => {
    if (window.getSelection) {
      const range = document.createRange();
      range.selectNode(element);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
    }
  };

  private prepareInsightboardJson = () => {
    const cleanObject = [
      {
        name: this.$scope.insightboard.tag,
        title: this.$scope.insightboard.name,
        widgets: [],
      },
    ];

    for (const item of this.$scope.insightboard.insightBoardItems) {
      const newObject = {
        name: item.insightName,
        sizeX: item.sizeX,
        sizeY: item.sizeY,
        row: item.row,
        col: item.col,
      };

      cleanObject[0].widgets.push(newObject);
    }

    return YAML.stringify(cleanObject);
  };

  private setInsightLoading = (filterKey: string) => {
    if (this.insightsParametersUsageMap[filterKey]) {
      this.insightsParametersUsageMap[filterKey].forEach((insightId) => {
        const currentInsight = this.$scope.insightsIdMap[insightId];
        currentInsight.loading =
          currentInsight.insight.state.status === "connected" || (currentInsight.insight.source && currentInsight.insight.source.sourceApplication !== "marketplace")
            ? true
            : false;
      });
    }
  };

  // get InsightIds not affected by the filter changes, i.e. insights to exclude from the recalculation list
  private getExcludeInsightIds = (filterChanges: IInsightboardItemSetting[]): string[] => {
    if (!filterChanges.length) {
      return [];
    }

    const includeInsightIds = new Set<string>();
    filterChanges.forEach((currentFilter) => {
      this.insightsParametersUsageMap[currentFilter.name].forEach((insightId) => {
        includeInsightIds.add(insightId);
      });
    });

    return this.$scope.insightboard.insights.filter((insight) => !includeInsightIds.has(insight.insightId)).map((insight) => insight.insightId);
  };

  private onFiltersChanged = (valueMap: Record<string, IInsightboardItemSetting>): void => {
    const filterChanges = this.getFilterChanges(valueMap, this.previousFiltersMap);
    filterChanges.forEach((currentFilter) => {
      this.setInsightLoading(currentFilter.name);
    });

    const excludeInsightIds: string[] = this.getExcludeInsightIds(filterChanges);

    this.calculateInsightboardWidgets(this.$scope.insightboard.id, Object.values(valueMap), excludeInsightIds).then(
      (col) => {
        this.processTransactions(col.items);
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private getDisplayNames = (filterKey: string, entitiesIds: string, entitiesMap: ICachedDynamicEntitiesMap): string => {
    const newDisplayNames: string[] = [];

    const persistedFilterMap = this.generatePersistedFiltersMap()[filterKey];
    if (entitiesMap) {
      const entitiesIdsArr = entitiesIds.split(",");
      for (let s = 0; s < entitiesIdsArr.length; s++) {
        const entityId = entitiesIdsArr[s];
        const displayName = entitiesMap[entityId] ? entitiesMap[entityId] : persistedFilterMap ? persistedFilterMap[entityId] : null;
        if (displayName) {
          newDisplayNames.push(displayName);
        }
      }
    }

    return newDisplayNames.toString();
  };

  private updateReportFilter = (...updatedFilters: IUpdatedFilter[]): void => {
    const persistedFilter = angular.copy(this.reportFilter);
    const updatedFilterMap: { [name: string]: IInsightboardItemSetting } = {};

    updatedFilters.forEach((updatedFilter) => {
      this.$scope.selectedParameters[updatedFilter.filterKey] = updatedFilter.value;

      const updatedFilterItem: IInsightboardItemSetting = {
        name: updatedFilter.filterKey,
        value: updatedFilter.value,
        valueDisplayName:
          updatedFilter.updatedValueDisplayName || this.getDisplayNames(updatedFilter.filterKey, updatedFilter.value, updatedFilter.cachedDynamicEntitiesMap),
      };

      updatedFilterMap[updatedFilterItem.name] = updatedFilterItem;

      persistedFilter.dashboardItemsSettings?.forEach((persistedFilter) => {
        if (persistedFilter.name === updatedFilterItem.name) {
          persistedFilter.value = updatedFilterItem.value;
          persistedFilter.valueDisplayName = updatedFilterItem.valueDisplayName;
        }
      });
    });

    this.$scope.indicators.savingFilters = true;
    if (persistedFilter.id) {
      this.hubService.patchFilter(persistedFilter.id, { dashboardItemsSettings: persistedFilter.dashboardItemsSettings }).then(
        () => {
          this.$scope.heatmapStart = this.getHeatmapStart(this.$scope.sessionIdMap, this.$scope.selectedParameters);

          this.reportFilter = persistedFilter;
          this.$scope.indicators.savingFilters = false;
          this.$scope.indicators.insightboard = true;
          this.calculateInsightboard(this.$scope.insightboard.id, this.reportFilter.dashboardItemsSettings);
        },
        (error) => {
          this.$scope.indicators.savingFilters = false;
          this.uiErrorHandlingService.handleModal(error);
        }
      );
    } else {
      this.hubService.createFilter(persistedFilter).then(
        (filter: IReportFilter) => {
          this.reportFilter = filter;
          this.$scope.indicators.savingFilters = false;
          this.$scope.indicators.insightboard = true;

          this.calculateInsightboard(this.$scope.insightboard.id, this.reportFilter.dashboardItemsSettings);
        },
        (error) => {
          this.$scope.indicators.savingFilters = false;
          this.uiErrorHandlingService.handleModal(error);
        }
      );
    }
  };

  private getHeatmapStart = (sessionIdMap: ISessionsIdMap, selectedParameters: IInsightFilters): Date => {
    // no session filter
    if (!selectedParameters.session || selectedParameters.session === "") {
      return dayjs().subtract(11, "month").toDate();
    }

    const sessionsIds = selectedParameters.session.split(",");
    const dates = sessionsIds.reduce((agg, id, i) => {
      agg[i] = dayjs(sessionIdMap[id].start);
      return agg;
    }, []);

    // session with oldest start from all sessions in filter
    return dayjs.min(dates).toDate();
  };

  private calculateInsightboard = (id: string, dashboardItemsSettings: IInsightboardItemSetting[]) => {
    this.calculateInsightboardWidgets(id, dashboardItemsSettings).then(
      (col) => {
        this.$scope.indicators.insightboard = false;

        this.processTransactions(col.items);
      },
      (err) => this.handleIndicatorError(err, "insightboard")
    );
  };

  private calculateInsightboardWidgets(
    insightboardId: string,
    parameters: IInsightboardItemSetting[] = null,
    excludeInsightIds: string[] = null
  ): IPromise<ICollection<IInsightboardTransactions>> {
    // Make sure insights socket is open before calculating insights data
    this.$scope.indicators.insightboard = false;

    this.insightboardService.getCachedInsightboardWidgets(insightboardId, parameters).then(
      (insightsResponse) => {
        insightsResponse.forEach((insightResponse) => {
          const insight = this.$scope.insightsIdMap[insightResponse.insightId];
          if (insight) {
            insight.calculationResult = insightResponse.data;
            insight.loading = false;
          }
        });
      },
      (err) => this.handleIndicatorError(err, "insightboard")
    );

    return this.messagingService.onInsightsSocketOpen().then(() => this.insightboardService.calculateInsightboardWidgets(insightboardId, parameters, excludeInsightIds));
  }

  private requestInsightCalculationById(insightId: string, filters: IInsightboardItemSetting[] = null) {
    this.insightService.requestInsightCalculationById(insightId, filters).then(
      (transaction) => {
        const insightboardTransaction: IInsightboardTransactions = {
          transactionId: transaction.transactionId,
          insightId: insightId,
        };

        this.processTransactions([insightboardTransaction]);
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  }

  private saveFilters = () => {
    this.$scope.indicators.savingFilters = true;

    this.$scope.insightboard.settings = this.$scope.filters.map((f) => createInsightboardSettingFromGridFilter(f));

    this.insightboardService.editInsightboardSettings(this.$scope.insightboard.id, this.$scope.insightboard.name, this.$scope.insightboard.settings).then(
      () => {
        this.$scope.indicators.savingFilters = false;

        this.$scope.filterChanged = false;
      },
      (err) => {
        this.$scope.indicators.savingFilters = false;
        this.uiErrorHandlingService.handleModal(err);
      }
    );
  };

  private generatePersistedFiltersMap = (): { [filterName: string]: ICachedDynamicEntitiesMap } => {
    const persistedFiltersMap = {};
    if (this.reportFilter && this.reportFilter.dashboardItemsSettings) {
      this.reportFilter.dashboardItemsSettings.forEach((filter) => {
        persistedFiltersMap[filter.name] = this.generatePersistedFilterItemsMap(filter);
      });
    }
    return persistedFiltersMap;
  };

  private generatePersistedFilterItemsMap = (filter: IInsightboardItemSetting): ICachedDynamicEntitiesMap => {
    const persistedFilterItemsMap = {};
    if (filter && filter.value && filter.valueDisplayName) {
      const itemIds: string[] = filter.value.split(",");
      const itemDisplayNames: string[] = filter.valueDisplayName.split(",");
      for (let i = 0; i < itemIds.length; i++) {
        const itemId = itemIds[i];
        const itemDisplayName = itemDisplayNames[i];
        persistedFilterItemsMap[itemId] = itemDisplayName || "";
      }
    }
    return persistedFilterItemsMap;
  };

  private onInsightRemoved = (ev: IAngularEvent, removedWidget: IInsightboardItem, savedWidgets: IInsightboardItem[]): void => {
    this.$scope.insightboard.insightBoardItems = this.$scope.insightboard.insightBoardItems.filter(
      (widget: IInsightboardItem) => widget.insightId !== removedWidget.insightId
    );
    this.initialWidgets = this.deepCopyWidgets(savedWidgets);
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private getReportFilter = (filters: IReportFilter[]): IReportFilter => {
    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i];

      if (filter.name === this.$scope.insightboard.name) {
        return filter;
      }
    }

    this.defaultReportFilter.name = this.$scope.insightboard.name;
    return this.defaultReportFilter;
  };

  private translateReportName = (name: string) => {
    const key = name.toLowerCase().replace(" ", "_");
    const translatedName = localize(key);

    return translatedName || name;
  };

  private trackPage = (insightboard: IInsightboard) => {
    let page = "";
    if (insightboard.isReport) {
      page = "gtmhub.reports." + insightboard.name.replace(" ", ".").toLowerCase();
    } else {
      page = "gtmhub.insightboard";
    }

    this.analyticsService.page(page, { controller: "InsightboardCtrl" });
  };

  private redirectTo = (widget: IInsightboardItem): void => {
    const missingConnector = widget.insightState.metadata.connectorName;
    const missingEntitiesOnly = widget.insightRequiredEntities.filter((requiredEntity) => requiredEntity.isMissing);

    const params = {
      returnState: "gtmhub.insightboard",
      dataSourceBlueprintName: missingConnector,
      requiredEntities: missingEntitiesOnly,
    };

    switch (widget.insightState.metadata.type) {
      case "connector":
        this.$state.go(".add.connectorConfigure", params);
        break;
      case "entity":
        this.$state.go(".add.connectorSelector", params);
        break;
    }
  };

  private clearDeletedFilterValues = (settingsArr: IInsightboardItemSetting[]): { settings: IInsightboardItemSetting[]; settingsChanged: boolean } => {
    const settings = angular.copy(settingsArr);
    const deletedIds = {
      [IInsightboardItemSettingName.session]: [],
      [IInsightboardItemSettingName.team]: [],
    };
    const scopeCollectionsMap = {
      [IInsightboardItemSettingName.session]: "sessionIdMap",
      [IInsightboardItemSettingName.team]: "teamsIdMap",
    };

    return {
      settings: settings.map((item) => {
        if (item.name in IInsightboardItemSettingName) {
          const persistedFilterItemIds = item.value ? item.value.split(",") : [];
          const persistedFilterItemNames = item.valueDisplayName ? item.valueDisplayName.split(",") : [];

          item.value = persistedFilterItemIds
            .filter((persistedId, idx) => {
              if (!this.$scope[scopeCollectionsMap[item.name]][persistedId]) {
                deletedIds[item.name].push(idx);
                return false;
              }

              return true;
            })
            .join(",");
          item.valueDisplayName = persistedFilterItemNames.filter((persistedName, idx) => !deletedIds[item.name].includes(idx)).join(",");
        }

        return item;
      }),
      settingsChanged: Object.values(deletedIds).filter((idsCollection) => idsCollection.length).length ? true : false,
    };
  };

  private setInitialRenderingLimits = (insights: IInsightboardItem[]): number => {
    if (insights.length === 0) return 1;

    const windowHeight = document.documentElement.clientHeight;

    let thereIsMoreRoom = true;

    while (thereIsMoreRoom) {
      if (!insights[this.gridStates.insightIndex]) return --this.gridStates.insightIndex;

      this.gridStates.columnsOccupied += insights[this.gridStates.insightIndex].sizeX;
      this.gridStates.rowsCount = Math.ceil(this.gridStates.columnsOccupied / this.gridStates.columns);
      this.gridStates.maxRowHeight =
        this.gridStates.maxRowHeight < insights[this.gridStates.insightIndex].sizeY ? insights[this.gridStates.insightIndex].sizeY : this.gridStates.maxRowHeight;
      this.gridStates.insightIndex++;

      const hasVerticalRoom = this.isThereVerticalRoom(windowHeight, this.gridStates);
      const hasHorizontalRoom = this.isThereHorizontalRoom(insights, this.gridStates);

      thereIsMoreRoom = hasVerticalRoom || hasHorizontalRoom;

      this.gridStates.rowsCount++;

      if (insights.length === 1) {
        break;
      }
    }

    // We want to return more insights than what could be displayed on your screen initially
    // The goal is to show the user that there are more insights and he can start scrolling to see more.
    // Hence we're adding LOAD_MORE_INSIGHTS_BY more insights to the initial rendering limits
    return this.gridStates.insightIndex + LOAD_MORE_INSIGHTS_BY;
  };

  private initGridStates(): IGridStates {
    return {
      insightIndex: 0,
      columnsOccupied: 0,
      maxRowHeight: 0,
      rowsCount: 1,
      columns: this.$scope.gridsterOptions.columns,
      rowHeight: Number(this.$scope.gridsterOptions.rowHeight),
    };
  }

  private isThereVerticalRoom(windowHeight: number, gridStates: IGridStates): boolean {
    const enlargementCoefficient = 0.6;
    const { rowsCount, maxRowHeight, rowHeight } = gridStates;
    const requiredHeight = rowsCount * maxRowHeight * rowHeight;

    return windowHeight * enlargementCoefficient - requiredHeight > maxRowHeight * rowHeight;
  }

  private isThereHorizontalRoom(insights: IInsightboardItem[], gridStates: IGridStates): boolean {
    const { columnsOccupied, insightIndex, columns, rowsCount } = gridStates;
    if (insights[insightIndex]) {
      return Math.ceil((columnsOccupied + insights[insightIndex].sizeX) / columns) <= rowsCount;
    } else {
      return false;
    }
  }
  public getSuggestionsSettings = (insightboardId: string, shouldInitializeEmptySettings: boolean): IInsightTemplateSuggestionSetting | null => {
    let suggestionsSettings: IInsightTemplateSuggestionSetting = this.insightTemplateSuggestionsService.getSettings(insightboardId);
    if (!suggestionsSettings && shouldInitializeEmptySettings) {
      suggestionsSettings = this.insightTemplateSuggestionsService.initializeWithDefaultSettings(this.$scope.insightboard.id);
    }

    return suggestionsSettings;
  };

  private onInsightSuggestionTemplateInstalled = (event: ng.IAngularEvent, insight: IInsight): void => {
    this.onWidgetsAdded([insight]);
    this.enterEditMode();
  };

  private onInsightSuggestionTemplateDismissed = (event: ng.IAngularEvent, settings: IInsightTemplateSuggestionSetting): void => {
    if (settings.linkToMarketplaceDismissed) {
      const shouldShowTooltip = this.insightTemplateSuggestionsService.shouldShowAddInsightsTooltip();
      this.$scope.showAddInsightsTooltip = shouldShowTooltip;
    }
    if (settings.numberOfSuggestionsToShow == 0 && settings.linkToMarketplaceDismissed) {
      this.$scope.shouldShowInsightSuggestions = false;
    }
  };

  private dismissTooltip = (): void => {
    this.$scope.showAddInsightsTooltip = false;
  };

  private directSubmitOfInsightboardToMarketplace = (): void => {
    this.$uibModal
      .open({
        windowClass: "medium",
        backdropClass: "dark-backdrop",
        component: "directSubmitInsightboard",
        backdrop: false,
        resolve: { insightboard: () => this.$scope.insightboard },
      })
      .result.then((reason) => {
        if (reason === "ok") {
          this.$uibModal.open({
            template: `
            <div class="d-flex flex-d-c justify-cont-c">
              <span class="gh-icon gh-icon-task-green gh-mb-2"></span>
              <span class="fw-500 fs-20px ink-90-v2">Insightboard was successfully submitted to the marketplace
              </span>
              <button gh-button size="small" kind="secondary" class="d-flex justify-cont-c gh-mt-2" ng-click="$close()">Close</button>
            </div>`,
            windowClass: "modal-small no-min-height",
          });
        }
      });
  };

  private addWidgetsFromStateParams = (insights: IInsight[]) => {
    if (insights && insights.length > 0) {
      this.onWidgetsAdded(insights);
      this.enterEditMode();
    }
  };
}
